//Libs
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import {
  CellContextMenuEvent,
  ColDef,
  GetQuickFilterTextParams,
  ICellRendererParams,
  RowClickedEvent,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy,
} from "ag-grid-community";
import isEqual from "lodash/isEqual";

//Local
import { ExistingEstimate } from "../../models/estimate";
import * as strings from "../../strings";
import { getEstimateStatus } from "../../assets/js/estimateFunctions";
import { ExistingCustomer } from "../../models/customer";
import { ExistingCustomerLocation } from "../../models/customer-location";
import { convertToReadableTimestamp } from "../../assets/js/convertToReadableTimestamp";
import { DollarCurrency } from "../../currency";
import { DbRead } from "../../database";
import SearchBox from "../../components/SearchBox";
import { CUSTOMERS_URL, ESTIMATES_URL_AUTH } from "../../urls";
import {
  getDefaultViewForThisTable,
  getTableViewPreferenceList,
  handleDeleteTableView,
  handleSaveNewTableViewPreference,
  handleSetDefaultView,
  setInitialView,
  TableViewPreference,
} from "../../models/table-view-preferences";
import DropdownTableSavedViews from "../../components/tables/DropdownTableSavedViews";
import EditTableViewsDialog from "../../components/tables/EditTableViewsDialog";
import SaveTableViewPreferenceDialog, {
  SaveTableViewPreferenceFormState,
} from "../../components/tables/SaveTableViewPreferenceDialog";
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";
import { phoneUtils } from "../../utils/phoneUtils";
import { logger } from "../../logging";

// Styles
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
import HeadingOne from "../../components/HeadingOne";

interface Props {
  estimateList: ExistingEstimate[];
  siteKey: string;
  goToViewEstimate: (estimate: ExistingEstimate) => void;
  children: {
    DateRangePicker?: React.ReactNode;
  };
}

export default function EstimateListPage(props: Props) {
  const [tableViewPreferenceDialogOpen, setTableViewPreferenceDialogOpen] =
    useState(false);
  const [editViewDialogsOpen, setEditViewDialogsOpen] = useState(false);
  const [tableViewPreferenceList, setTableViewPreferenceList] = useState<
    TableViewPreference[]
  >([]);
  const [selectedView, setSelectedView] = useState<TableViewPreference | null>(
    null,
  );
  const [customersList, setCustomers] = useState<ExistingCustomer[]>([]);
  const [customerLocationsList, setCustomerLocations] = useState<
    ExistingCustomerLocation[]
  >([]);

  const gridRef = useRef<AgGridReact>(null);

  useEffect(() => {
    async function getCustomerAndCustomerLocationForEstimate() {
      const customerPromises: Promise<ExistingCustomer>[] =
        props.estimateList.map((e) => {
          return DbRead.customers.get(props.siteKey, e.customerID);
        });
      const customerLocationPromises: Promise<ExistingCustomerLocation>[] =
        props.estimateList.map((e) => {
          return DbRead.customerLocations.getSingle(
            props.siteKey,
            e.customerLocationID,
          );
        });
      const customers = await Promise.all(customerPromises);
      const customerLocations = await Promise.all(customerLocationPromises);
      setCustomers(customers);
      setCustomerLocations(customerLocations);
    }

    getCustomerAndCustomerLocationForEstimate();
    return;
  }, [props.estimateList, props.siteKey]);

  const actionButtons = (
    <Fragment>
      <BaseButtonPrimary onClick={() => setTableViewPreferenceDialogOpen(true)}>
        {strings.SAVE_TABLE_VIEW}
      </BaseButtonPrimary>
      <BaseButtonSecondary onClick={() => setEditViewDialogsOpen(true)}>
        {strings.EDIT_TABLE_VIEWS}
      </BaseButtonSecondary>
    </Fragment>
  );

  const dropdownSavedViews = tableViewPreferenceList.length > 0 &&
    selectedView && (
      <DropdownTableSavedViews
        savedViewList={tableViewPreferenceList}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
      />
    );

  const saveTableViewPreferenceDialog = (
    <SaveTableViewPreferenceDialog
      closeDialog={() => setTableViewPreferenceDialogOpen(false)}
      isDialogOpen={tableViewPreferenceDialogOpen}
      handleSave={(formValues: SaveTableViewPreferenceFormState) =>
        handleSaveNewTableViewPreference(
          formValues,
          "estimateList",
          gridRef,
          setTableViewPreferenceList,
          setSelectedView,
        )
      }
    />
  );

  const editTableViewsDialog = (
    <EditTableViewsDialog
      isDialogOpen={editViewDialogsOpen}
      closeDialog={() => setEditViewDialogsOpen(false)}
      deleteTableView={(viewToDelete: TableViewPreference) =>
        handleDeleteTableView(
          viewToDelete,
          setTableViewPreferenceList,
          setSelectedView,
        )
      }
      handleSetDefaultView={(viewToSetAsDefault: TableViewPreference) =>
        handleSetDefaultView(
          viewToSetAsDefault,
          setTableViewPreferenceList,
          setSelectedView,
        )
      }
      savedViewList={tableViewPreferenceList}
    />
  );

  return (
    <div className="space-y-2 lg:space-y-6">
      <HeadingOne>{strings.ESTIMATES}</HeadingOne>
      <div className="ag-theme-alpine isolate h-[1000px] max-h-[68vh] pb-6">
        <EstimateListTable
          estimateList={props.estimateList}
          goToViewEstimate={props.goToViewEstimate}
          actionButtons={actionButtons}
          dropdownSavedViews={dropdownSavedViews}
          gridRef={gridRef}
          setSelectedView={setSelectedView}
          setTableViewPreferenceList={setTableViewPreferenceList}
          selectedView={selectedView}
          customersList={customersList}
          customerLocationsList={customerLocationsList}
          children={props.children}
        />
        {saveTableViewPreferenceDialog}
        {editTableViewsDialog}
      </div>
    </div>
  );
}

interface EstimateListTableProps {
  estimateList: Props["estimateList"];
  goToViewEstimate: Props["goToViewEstimate"];
  actionButtons: React.ReactNode;
  gridRef: React.MutableRefObject<any>;
  setSelectedView: React.Dispatch<
    React.SetStateAction<TableViewPreference | null>
  >;
  setTableViewPreferenceList: React.Dispatch<
    React.SetStateAction<TableViewPreference[]>
  >;
  dropdownSavedViews: React.ReactNode;
  selectedView: TableViewPreference | null;
  customersList: ExistingCustomer[];
  customerLocationsList: ExistingCustomerLocation[];
  children: {
    DateRangePicker?: React.ReactNode;
  };
}

const commonColDefs: ColDef = {
  // minWidth: 20,
  resizable: true,
  filter: true,
  sortable: true,
  headerClass:
    "px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase",
  flex: 1,
  tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
  tooltipField: "notes",
};

/* TABLE COMPONENT */
const EstimateListTable = memo(
  ({
    estimateList,
    goToViewEstimate,
    actionButtons,
    gridRef,
    setSelectedView,
    setTableViewPreferenceList,
    selectedView,
    dropdownSavedViews,
    customersList,
    customerLocationsList,
    children,
  }: EstimateListTableProps) => {
    const [gridReady, setGridReady] = useState(false);

    const autoSizeStrategy = useMemo<
      | SizeColumnsToFitGridStrategy
      | SizeColumnsToFitProvidedWidthStrategy
      | SizeColumnsToContentStrategy
    >(() => {
      return {
        type: "fitGridWidth",
        defaultMinWidth: 50,
        columnLimits: [
          {
            colId: "ID",
            minWidth: 50,
          },
        ],
      };
    }, []);

    const onGridReady = useCallback(() => {
      setGridReady(true);
    }, []);

    useLayoutEffect(() => {
      if (gridRef.current && gridReady) {
        gridRef.current.api?.sizeColumnsToFit();
        getTableViewPreferenceList("estimateList", setTableViewPreferenceList);
        const defaultView = getDefaultViewForThisTable("estimateList");
        setInitialView(defaultView, gridRef, setSelectedView);
      }
    }, [gridRef, setSelectedView, setTableViewPreferenceList, gridReady]);

    useEffect(() => {
      if (selectedView && gridReady) {
        gridRef.current?.api.applyColumnState({
          state: selectedView.settings,
          applyOrder: true,
        });
        gridRef.current?.api.setFilterModel(selectedView.filter);
        logger.info("set column state for", selectedView.name);
      }
    }, [gridRef, selectedView, gridReady]);

    const onFilterTextBoxChanged = useCallback(() => {
      if (gridReady) {
        gridRef.current?.api.setQuickFilter(
          (document.getElementById("filter-text-box") as HTMLInputElement)
            .value,
        );
      }
    }, [gridRef, gridReady]);

    const renderCustomerName = (params: ICellRendererParams): JSX.Element => {
      const currentCustomer = customersList.find(
        (customer) => customer.id === params.data.customerID,
      );
      return <div className="capitalize">{currentCustomer?.name ?? ""}</div>;
    };

    const renderCustomerPhone = (params: ICellRendererParams): JSX.Element => {
      const currentCustomer = customersList.find(
        (customer) => customer.id === params.data.customerID,
      );
      return (
        <div className="capitalize">
          {currentCustomer?.phone && currentCustomer.phone.length > 0
            ? phoneUtils.display(currentCustomer.phone[0])
            : null}
        </div>
      );
    };

    const renderCustomerLocation = (
      params: ICellRendererParams,
    ): JSX.Element => {
      const currentCustomerLocation = customerLocationsList.find(
        (customerLocation) =>
          customerLocation.id === params.data.customerLocationID,
      );
      return (
        <div className="capitalize">
          {currentCustomerLocation?.fullAddress ?? ""}
        </div>
      );
    };

    const columnDefs: ColDef[] = [
      {
        field: "estimateNumber",
        headerName: "#",
        maxWidth: 80,
        tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
        tooltipField: "estimateNumber",
      },
      {
        field: "status",
        headerName: "Status",
        cellRenderer: (params: ICellRendererParams) =>
          getEstimateStatus(params.data.status),
      },
      {
        field: "customerID",
        headerName: "Customer",
        cellRenderer: renderCustomerName,
        getQuickFilterText: (params: GetQuickFilterTextParams) => {
          return getNameStringForFilter(params, customersList);
        },
      },
      {
        field: "customerLocationID",
        headerName: "Customer Address",
        cellRenderer: renderCustomerLocation,
        getQuickFilterText: (params: GetQuickFilterTextParams) => {
          return getAddresstringForFilter(params, customerLocationsList);
        },
      },
      {
        field: "customerID",
        headerName: "Customer Phone",
        cellRenderer: renderCustomerPhone,
      },
      {
        field: "timestampSentToCustomer",
        headerName: "Sent to Customer",
        cellRenderer: (params: ICellRendererParams) =>
          getReadableTimestamp(params.data.timestampSentToCustomer),
        getQuickFilterText: (params: GetQuickFilterTextParams) =>
          getReadableTimestamp(params.data.timestampSentToCustomer),
      },
      {
        field: "timestampCreated",
        headerName: "Created",
        cellRenderer: (params: ICellRendererParams) =>
          getReadableTimestamp(params.data.timestampCreated),
        getQuickFilterText: (params: GetQuickFilterTextParams) =>
          getReadableTimestamp(params.data.timestampCreated),
      },
      {
        field: "customData.totalAmount",
        headerName: "Total Amount",
        cellRenderer: getFormattedAmount,
        getQuickFilterText: getFormattedAmount,
      },
    ];

    return (
      <Fragment>
        <div className="flex flex-col-reverse justify-between gap-6 md:-mt-4 md:flex-row md:items-end lg:mt-0">
          <SearchBox
            onInput={onFilterTextBoxChanged}
            widthClasses="w-full max-w-sm"
          />
          <span className="flex flex-wrap items-center justify-end gap-4 md:flex-col md:items-end lg:flex-row lg:items-center">
            {children?.DateRangePicker}
            {dropdownSavedViews}
            <div className="flex gap-4">{actionButtons}</div>
          </span>
        </div>
        <AgGridReact
          reactiveCustomComponents
          ref={gridRef}
          onGridReady={onGridReady}
          tooltipShowDelay={1}
          defaultColDef={commonColDefs}
          className="mt-5 shadow"
          rowData={estimateList}
          animateRows={true}
          rowSelection="single"
          rowHeight={50}
          cacheQuickFilter={true}
          autoSizeStrategy={autoSizeStrategy}
          columnDefs={columnDefs}
          onRowClicked={(event: RowClickedEvent) =>
            goToViewEstimate(event.data)
          }
          onCellContextMenu={(event: CellContextMenuEvent<any, any>) => {
            const url = `${CUSTOMERS_URL}${ESTIMATES_URL_AUTH}/${event.data.id}`;
            return window.open(url, "_blank");
          }}
        />
      </Fragment>
    );
  },
  (previous, next) => {
    const isEstimateListTheSame = isEqual(
      previous.estimateList,
      next.estimateList,
    );

    const isDropdownTheSame = isEqual(
      previous.dropdownSavedViews,
      next.dropdownSavedViews,
    );

    const isCustomerListTheSame = isEqual(
      previous.customersList,
      next.customersList,
    );
    const isCustomerLocationsListTheSame = isEqual(
      previous.customerLocationsList,
      next.customerLocationsList,
    );

    return (
      isEstimateListTheSame &&
      isDropdownTheSame &&
      isCustomerListTheSame &&
      isCustomerLocationsListTheSame
    );
  },
);

function getNameStringForFilter(
  params: GetQuickFilterTextParams,
  customerList: ExistingCustomer[],
): string {
  const currentCustomer = customerList.find(
    (customer) => customer.id === params.data.customerID,
  );

  return currentCustomer ? currentCustomer.name : "";
}

function getAddresstringForFilter(
  params: GetQuickFilterTextParams,
  customerLocationList: ExistingCustomerLocation[],
): string {
  const currentCustomerLocation = customerLocationList.find(
    (customerLocation) =>
      customerLocation.id === params.data.customerLocationID,
  );

  return currentCustomerLocation && currentCustomerLocation.fullAddress
    ? currentCustomerLocation.fullAddress
    : "";
}

function getFormattedAmount(
  params: ICellRendererParams | GetQuickFilterTextParams,
): string {
  if (typeof params.data.customData.totalAmount !== "number") return "";
  return DollarCurrency.format(params.data.customData.totalAmount);
}

function getReadableTimestamp(timestamp: unknown): string {
  if (timestamp != null) {
    return convertToReadableTimestamp(timestamp);
  } else {
    return "";
  }
}
